// Popup service for frontend-classic
import { getApiBaseUrl } from '../lib/apiBaseUrl';

const POPUP_FETCH_TIMEOUT_MS = 3_500;

const fetchWithTimeout = async (
    input: RequestInfo | URL,
    init: RequestInit,
    timeoutMs: number = POPUP_FETCH_TIMEOUT_MS
): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
        return await fetch(input, { ...init, signal: controller.signal });
    } finally {
        window.clearTimeout(timeoutId);
    }
};

export interface PopupData {
    _id: string;
    tenantId: string;
    title: string;
    content: string;
    isActive: boolean;
    priority: number;
    forceShow: boolean;
    forceShowUntil?: Date | null;
    showOnRoutes: string[];
    popupType: 'info' | 'warning' | 'success' | 'announcement';
    buttonText: string;
    allowDismiss: boolean;
    autoCloseAfter?: number | null;
    startDate?: Date;
    endDate?: Date | null;
    // Image support
    imageUrl?: string | null;
    imagePosition?: 'top' | 'left' | 'right' | 'bottom' | 'background' | 'center';
    imageAlt?: string;
    imageStyle?: 'cover' | 'contain' | 'auto' | 'fill';
    displayMode?: 'auto' | 'image_only' | 'text_only' | 'image_text';
    isForceShow?: boolean; // Added by backend
    createdAt: Date;
    updatedAt: Date;
}

export interface ActivePopupsResponse {
    success: boolean;
    data: {
        popups: PopupData[];
        count: number;
        route: string;
        tenantId: string;
    };
}

export interface DismissResponse {
    success: boolean;
    data: {
        dismissal: any;
        message: string;
    };
}

class PopupService {
    private readonly baseUrl: string;

    constructor() {
        this.baseUrl = getApiBaseUrl();
    }

    // Public method to get base URL
    getBaseUrl(): string {
        return this.baseUrl;
    }

    // Generate client fingerprint for anonymous users
    private generateClientFingerprint(): string {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.textBaseline = 'top';
            ctx.font = '14px Arial';
            ctx.fillText('Client fingerprint', 2, 2);
        }

        const fingerprint = [
            navigator.userAgent,
            navigator.language,
            screen.width + 'x' + screen.height,
            new Date().getTimezoneOffset(),
            canvas.toDataURL()
        ].join('|');

        // Simple hash function
        let hash = 0;
        for (let i = 0; i < fingerprint.length; i++) {
            const char = fingerprint.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }

        return Math.abs(hash).toString(36);
    }

    // Get session ID from localStorage or generate new one
    private getSessionId(): string {
        let sessionId = localStorage.getItem('popup-session-id');
        if (!sessionId) {
            sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('popup-session-id', sessionId);
        }
        return sessionId;
    }

    // Fetch active popups for current route
    async getActivePopups(tenantId: string, route: string = '/'): Promise<PopupData[]> {
        try {
            const params = new URLSearchParams({
                tenantId: tenantId,
                route: route
            });

            const response = await fetchWithTimeout(`${this.baseUrl}/api/v1/configuration-service/popups/${tenantId}/active?${params}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Session-ID': this.getSessionId(),
                    'X-Client-Fingerprint': this.generateClientFingerprint()
                }
            });

            if (!response.ok) {
                // Enhanced error handling for rate limiting
                if (response.status === 429) {
                    console.warn('⚠️ Rate limited - popup service will retry automatically');
                    throw new Error('429'); // Special marker for rate limiting
                }
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data: ActivePopupsResponse = await response.json();

            if (!data.success) {
                throw new Error('Failed to fetch popups');
            }

            console.log(`🎪 Fetched ${data.data.popups.length} active popups for route: ${route}`);
            return data.data.popups;

        } catch (error) {
            console.error('Failed to fetch active popups:', error);
            return [];
        }
    }

    // Dismiss a popup
    async dismissPopup(popupId: string, tenantId: string, dismissType: 'manual' | 'auto' | 'timeout' = 'manual'): Promise<boolean> {
        try {
            // Note: identifier is sent via headers for API tracking

            const response = await fetchWithTimeout(`${this.baseUrl}/api/v1/configuration-service/popups/${tenantId}/${popupId}/dismiss`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Session-ID': this.getSessionId(),
                    'X-Client-Fingerprint': this.generateClientFingerprint()
                },
                body: JSON.stringify({
                    tenantId: tenantId,
                    dismissType: dismissType
                })
            }, 5_000);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data: DismissResponse = await response.json();

            if (data.success) {
                console.log(`✅ Popup ${popupId} dismissed successfully`);

                return true;
            } else {
                throw new Error(data.data?.message || 'Failed to dismiss popup');
            }

        } catch (error) {
            console.error('Failed to dismiss popup:', error);
            return false;
        }
    }

    // Store popup dismissal in localStorage
    private storePopupDismissal(popupId: string, tenantId: string, dismissType: string): void {
        try {
            const dismissalKey = `popup-dismissed-${tenantId}-${popupId}`;
            const dismissalData = {
                popupId,
                tenantId,
                dismissType,
                dismissedAt: new Date().toISOString(),
                permanent: dismissType === 'manual' // Manual dismissals are permanent
            };

            localStorage.setItem(dismissalKey, JSON.stringify(dismissalData));
            console.log(`💾 Stored popup dismissal: ${popupId} (${dismissType})`);
        } catch (error) {
            console.warn('Failed to store popup dismissal:', error);
        }
    }

    // Check if popup is dismissed
    isPopupDismissed(popupId: string, tenantId: string): boolean {
        try {
            // Check new dismissal system (individual popup dismissals)
            const dismissalKey = `popup-dismissed-${tenantId}-${popupId}`;
            const dismissalData = localStorage.getItem(dismissalKey);

            if (dismissalData) {
                const dismissal = JSON.parse(dismissalData);
                console.log(`🚫 Popup ${popupId} is dismissed (${dismissal.dismissType})`);
                return true;
            }

            // Check legacy dismissal system (array format)
            const legacyKey = `dismissed-popups-${tenantId}`;
            const legacyData = localStorage.getItem(legacyKey);
            if (legacyData) {
                const dismissedArray = JSON.parse(legacyData);
                if (dismissedArray.includes(popupId)) {
                    console.log(`🚫 Popup ${popupId} is dismissed (legacy system)`);
                    return true;
                }
            }

            return false;
        } catch (error) {
            console.warn('Failed to check popup dismissal:', error);
            return false;
        }
    }

    // Clear all popup dismissals for a tenant
    clearPopupDismissals(tenantId: string): void {
        try {
            const keys = Object.keys(localStorage);

            // Clear new dismissal system (individual popup dismissals)
            const dismissalKeys = keys.filter(key => key.startsWith(`popup-dismissed-${tenantId}-`));
            dismissalKeys.forEach(key => {
                localStorage.removeItem(key);
            });

            // Clear legacy dismissal system (array format)
            const legacyKey = `dismissed-popups-${tenantId}`;
            if (localStorage.getItem(legacyKey)) {
                localStorage.removeItem(legacyKey);
            }

            console.log(`🗑️ Cleared ${dismissalKeys.length} popup dismissals + legacy system for tenant ${tenantId}`);
        } catch (error) {
            console.warn('Failed to clear popup dismissals:', error);
        }
    }

    // Get popup type badge style
    getPopupTypeStyle(type: string): { bgColor: string; textColor: string; icon: string } {
        switch (type) {
            case 'info':
                return { bgColor: 'bg-blue-50', textColor: 'text-blue-700', icon: 'ℹ️' };
            case 'warning':
                return { bgColor: 'bg-yellow-50', textColor: 'text-yellow-700', icon: '⚠️' };
            case 'success':
                return { bgColor: 'bg-green-50', textColor: 'text-green-700', icon: '✅' };
            case 'announcement':
                return { bgColor: 'bg-purple-50', textColor: 'text-purple-700', icon: '📢' };
            default:
                return { bgColor: 'bg-gray-50', textColor: 'text-gray-700', icon: 'ℹ️' };
        }
    }

    // Get popup type label in Thai
    getPopupTypeLabel(type: string): string {
        switch (type) {
            case 'info':
                return 'ข้อมูล';
            case 'warning':
                return 'คำเตือน';
            case 'success':
                return 'สำเร็จ';
            case 'announcement':
                return 'ประกาศ';
            default:
                return type;
        }
    }

    // Check if popup should auto-close
    shouldAutoClose(popup: PopupData): boolean {
        return popup.autoCloseAfter !== null && popup.autoCloseAfter !== undefined && popup.autoCloseAfter > 0;
    }

    // Get auto-close timeout in milliseconds
    getAutoCloseTimeout(popup: PopupData): number {
        if (!this.shouldAutoClose(popup)) return 0;
        return (popup.autoCloseAfter || 0) * 1000; // Convert seconds to milliseconds
    }

    // Check if popup is force show
    isForceShow(popup: PopupData): boolean {
        return popup.isForceShow === true || popup.forceShow === true;
    }

    // Legacy method - redirect to new system
    storeDismissedPopup(popupId: string, tenantId: string): void {
        // Use new dismissal system
        this.storePopupDismissal(popupId, tenantId, 'manual');
    }

    // Legacy method - redirect to new system  
    isPopupDismissedLocally(popupId: string, tenantId: string): boolean {
        // Use new dismissal system
        return this.isPopupDismissed(popupId, tenantId);
    }

    // Legacy method - redirect to new system
    clearDismissedPopups(tenantId: string): void {
        // Use new dismissal system
        this.clearPopupDismissals(tenantId);
    }
}

export const popupService = new PopupService();
